import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '../../../components/modal/modal.component';
import {IonAlert, useIonViewWillLeave} from '@ionic/react';
import moment, {Moment} from "moment";
import {Order} from "../reportPage.component";
import {
    checkCameraPermission,
    openSettings,
    startContinuousScan,
    makeWebViewHidden,
    restoreWebView,
    toggleTorch,
    stopAllScanning,
    ContinuousScanHandle,
} from "@services/barcodeScanner.service";

type QrScannerModalProps = {
    isOpen: boolean;
    order?: Order;
    onClose: () => void;
    onSubmit: (value: string) => void;
};

const SimpleScannerModal: React.FC<QrScannerModalProps> = (props: QrScannerModalProps) => {

    const {t} = useTranslation();
    const [showModal, updateShowModal] = useState<boolean>(false);
    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
    const [scanStartAt, setScanStartAt] = useState<Moment|undefined>();
    const [scanIntervalAt, setScanIntervalAt] = useState<Moment|undefined>();

    const scanHandleRef = useRef<ContinuousScanHandle | null>(null);

    const handleClose = () => {
        props.onClose();
        stopScan();
    };

    useEffect(() => {
        handleOpen();
    }, [props.isOpen]);

    const handleOpen = () => {
        updateShowModal(props.isOpen);
        if (props.isOpen) {
            setScanStartAt(moment());
            setScanIntervalAt(moment());
            scanLabel();
        }
    };

    const onSubmit = async (value: string) => {
        props.onSubmit(value);
    };

    let scanTimeout: NodeJS.Timeout | undefined;

    useEffect(() => {
        checkScanInterval();
        if (scanTimeout) clearInterval(scanTimeout);

        if (scanStartAt) {
            scanTimeout = setInterval(checkScanInterval, 1000);
        }

        return () => {
            if (scanTimeout) clearInterval(scanTimeout);
        };
    }, [scanIntervalAt])

    const checkScanInterval = () => {
        let now = moment();
        if (scanStartAt && now.diff(scanStartAt, 's') > (props.order?.ep2_scanner_timeout ? parseInt(props.order.ep2_scanner_timeout) : 15)) {
            stopScan();
        }
    }

    useIonViewWillLeave(() => {
        stopScan();
    });

    const startScan = async () => {
        let scanStarted:any = undefined;
        makeWebViewHidden();

        const attemptStart = () => startContinuousScan([], (rawValue) => {
            let now = moment();
            if (scanStarted === undefined) {
                scanStarted = now;
                setScanStartAt(now)
            }

            if (rawValue) {
                // Fully stop this scan session (native camera release) before
                // submitting the value - submitting can trigger an auto-transition
                // that immediately mounts and starts a new scanner instance, and
                // starting one while this one is still shutting down natively can
                // silently fail to show a camera preview.
                stopScan().finally(() => onSubmit(rawValue));
            }

            setScanIntervalAt(moment())

            if (now.diff(scanStarted, 's') > (props.order?.ep2_scanner_timeout ? parseInt(props.order.ep2_scanner_timeout) : 15)) {
                stopScan();
            }
        }, { enableTorch: props.order?.scanning_settings.torchDefault ?? false });

        try {
            scanHandleRef.current = await attemptStart();
        } catch (e) {
            // A previous scan session (e.g. from the field just scanned to trigger
            // this auto-opened modal) may still have been shutting down natively,
            // causing the start call to fail. Stop it explicitly and retry once
            // before giving up - otherwise the WebView is left hidden with no
            // camera ever drawn behind it.
            try {
                await stopAllScanning();
                scanHandleRef.current = await attemptStart();
            } catch (e2) {
                restoreWebView();
            }
        }
    };

    const stopScan = async () => {
        await scanHandleRef.current?.stop();
        scanHandleRef.current = null;
        restoreWebView();

        setScanStartAt(undefined)
        setScanIntervalAt(undefined)
        props.onClose();
        if (scanTimeout) clearInterval(scanTimeout);
    };

    const scanLabel = async () => {
        let hasPermission = await checkCameraPermission();
        if (!hasPermission) {
            setShowPermissionAlert(true);
            return;
        }
        startScan();
    }

    return (
        <Modal
            isOpen={showModal}
            disableClose={false}
            onDidDismiss={() => handleClose()}>

            {showModal && (
                <>
                    <div className="scanner-close-button">
                        <button onClick={() => handleClose()}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                    <div className="scanner-torch-button">
                        <button onClick={() => toggleTorch()}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="white">
                                <path d="M6 2h12l-2 6h2l-6 10v-6H8l2-6H6z"/>
                            </svg>
                        </button>
                    </div>
                </>
            )}

            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            openSettings();
                        }
                    }
                ]}
            />
        </Modal>
    );
};

export default SimpleScannerModal;
